import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { degreeCategories, degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { usePageMeta } from '../hooks/usePageMeta';
export function DegreeListingPage() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const categoryData = degreeCategories.find(c => c.id === category);
  usePageMeta(`Online ${categoryData?.name || 'Degree'} Programs | OnlineCollegeGuide`, `Browse accredited online ${categoryData?.name?.toLowerCase() || ''} degree programs. Compare schools, explore specializations, and find the right program for your career goals.`, {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    areaOfStudy: categoryData?.focusValue || ''
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const generalSpec = categoryData?.focusValue ? getSpecializationsForFocus(categoryData.focusValue).find(s => s.type.toLowerCase().startsWith('general') || s.type.toLowerCase().startsWith('all')) : undefined;
  // Fetch results from API
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load with the category's area of study
  useEffect(() => {
    if (categoryData?.focusValue) {
      fetchResults('', categoryData.focusValue, generalSpec?.value || '');
    }
  }, [categoryData?.focusValue, fetchResults, generalSpec?.value]);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);
  if (!categoryData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-867" data-mp="rjaiyd48at4r">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-868" data-mp="mls8giakc5un">
          Category Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-869" data-mp="10l1nvfqy2vxk">
          We couldn't find the degree category you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-870" data-mp="wkhqcb2glqaw">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-871" data-mp="2yb1jfkqm2ihy">
      {/* Page Header */}
      <div className="bg-navy text-white py-12" data-id="element-872" data-mp="2zrp5qd2wwixg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-873" data-mp="2zqx8kxto2d52">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-874" data-mp="34pqkwmx7vy3n">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-875" data-mp="38s58fkbtzm19">
              <li className="inline-flex items-center" data-id="element-876" data-mp="3dr8268md25tx">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-877" data-mp="3dqourluc6zu5">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-878" data-mp="2h3209tgtprjc" />
                  Home
                </Link>
              </li>
              <li data-id="element-879" data-mp="2l0g75eh17n86">
                <div className="flex items-center" data-id="element-880" data-mp="2q25k4znteayw">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-881" data-mp="2q0carclthcn7" />
                  <span className="ml-1 md:ml-2" data-id="element-882" data-mp="2usvqaf42ehun">Degrees</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-883" data-mp="1xdzzse0oanzl">
                <div className="flex items-center" data-id="element-884" data-mp="1xcye7fm16j3m">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-885" data-mp="22bje1dxdf0rj" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-886" data-mp="229yknrmslnv9">
                    {categoryData.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-id="element-887" data-mp="1kum6eks1vcfk">
            Online {categoryData.name} Degrees
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl" data-id="element-888" data-mp="1gs94y8kc60ol">
            {categoryData.description} Compare top accredited programs and find
            the right fit for your career goals.
          </p>
        </div>
      </div>

      <TrustSignals data-id="element-889" data-mp="1714p76mu6bsp" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-890" data-mp="1244jiwtwfb24">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-891" data-mp="124e94jb17g7a">
          {/* Main Content - Listings */}
          <div className="lg:w-3/4 order-2 lg:order-1" data-id="element-892" data-mp="x6utpg2lkxdr">
            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-893" data-mp="t5j25505y22x">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-894" data-mp="o4vdur3bjzce" />
                <p className="text-gray-600 font-medium" data-id="element-895" data-mp="efof3scxkv2x">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-896" data-mp="4jv4wfmopdob">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-897" data-mp="9i6h2x0gc09p" />
                <div data-id="element-898" data-mp="x8jzdvt8ikr3">
                  <p className="text-red-800 font-medium" data-id="element-899" data-mp="x80r76tdzaq9">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-900" data-mp="125aeogx7tcbg">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-901" data-mp="1g5tjlf6dw29e">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-902" data-mp="1kwjpctiq274m" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-903" data-mp="35myvp2jk8x8x" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-904" data-mp="30onn0v504ssa" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-905" data-mp="3ae71lolom0hc">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-906" data-mp="2mo30pozvoeu1">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-907" data-mp="2hq1jzapxcrlq" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-908" data-mp="2vpazs9a7xjp6">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-909" data-mp="2qp6du3qjjwrk">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar - Form */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-910" data-mp="q2wfpghucjf4">
            <div className="sticky top-24" data-id="element-911" data-mp="v0y0c5ux0qsp">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6" data-id="element-912" data-mp="v1zkr0xtmpn3">
                <div className="bg-[#1B365D] px-6 py-4" data-id="element-913" data-mp="z1qbx5r9f7s5">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-914" data-mp="13szz7zpc5z0v">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-915" data-mp="18s30ra63xn43">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-916" data-mp="1irk95zcquf3r">
                  {/* Degree Level */}
                  <div data-id="element-917" data-mp="1mrkodm7o2ubv">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-918" data-mp="1mprnknjqj4bs">
                      <option value="" data-id="element-919" data-mp="3c7etl0w3q1g2">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-920" data-mp="377sesn0mkd55">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-921" data-mp="3rbuhjj21l3c">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-922" data-mp="3r08hnapfvpwc">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-923" data-mp="3lzb2l6zq7bz2">
                      <option value="" data-id="element-924" data-mp="pmo517j3j88w">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-925" data-mp="ko4jv9cbu0hl">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-926" data-mp="tpce9gbq2h6e">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization / Program */}
                  <div data-id="element-927" data-mp="13ijnjc9l2hbr">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-928" data-mp="1hfyckfvxcn37">
                      <option value="" data-id="element-929" data-mp="393eidoybtw7w">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-930" data-mp="30at2cgm2y3mr">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-931" data-mp="2qcwsds4vpeq8">
                        {errors.specialization}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-[#D4A943] hover:bg-[#B38B2E] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-932" data-mp="2cfgnwm657v14">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-933" data-mp="d6yngh6dms0b" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Related Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-934" data-mp="460rmm63dqst">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-935" data-mp="3vdmptza1l12a">
                  Programs in {categoryData.name}
                </h4>
                <ul className="space-y-2" data-id="element-936" data-mp="rutfdvck8scc">
                  {getSpecializationsForFocus(formData.areaOfStudy || categoryData.focusValue).map(spec => <li key={spec.value} data-id="element-937" data-mp="n3bltscv9u1e">
                      <button onClick={() => {
                    updateFormData('specialization', spec.value);
                    fetchResults(formData.degreeInterest, formData.areaOfStudy || categoryData.focusValue, spec.value);
                  }} className={`text-sm flex items-center w-full text-left ${formData.specialization === spec.value ? 'text-crimson font-semibold' : 'text-gray-600 hover:text-crimson'}`} data-id="element-938" data-mp="i5jmid9w75n6">
                        <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-939" data-mp="1n3x021pqm7jj" />
                        {spec.type}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProgramContentSection degreeInterest={formData.degreeInterest} areaOfStudy={formData.areaOfStudy || categoryData.focusValue} specialization={formData.specialization || generalSpec?.value || ''} data-id="element-940" data-mp="1x09tqzla8lea" />
    </main>;
}